// ** React Imports
import { useState, useEffect, MouseEvent, useCallback, ReactElement } from 'react'

// ** MUI Imports
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'
import { DataGrid, GridEventListener } from '@mui/x-data-grid';
import Typography from '@mui/material/Typography'

// ** Store Imports
import { SelectOption } from 'src/types/common';

// ** Custom Components Imports
import TableHeader from 'src/features/sprints/TableHeader'
import EditSprintDrawer from 'src/features/sprints/EditSprintDrawer'
import { client } from 'src/utils/axios-client'
import { getSprintsEndpoint } from 'src/configs/endpoints'
import { SprintStore, Sprint } from 'src/stores/SprintStore'

import _ from 'lodash'
import { v4 as uuidv4 } from 'uuid'
import DateTimeDisplay from '../common/DateTimeDisplay'
import SprintRowOptions from './SprintRowOptions'
import LeadList from './LeadList'
import { FormControl, FormControlLabel, FormGroup, FormLabel, IconButton, Switch } from '@mui/material'
import LeadSearch from '../filters/leads/LeadSearch'
import { Filter, FilterType } from '../filters/FilterTypes'
import { v4 as uuid } from 'uuid';
import RoleSelector from '../roles/RoleSelector'
import RenderIf, { ClaimType, ClaimValue } from '../common/RenderIf'
import DeleteIcon from '@mui/icons-material/Delete';
import useAwaitableComponent from '../../hooks/useAwaitableComponent'
import ConfirmationDialog from '../common/ConfirmationDialog'
import { ApiResponseType } from '../../types'
import ApplicationStore from '../../stores/ApplicationStore'
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import PageHeader from '../../materio/@core/components/page-header'
import ICPWizard from './icp-wizard/components/ICPWizard';
import { AssignLeads } from './assign-leads/components/AssignLeads';

interface CellType {
    row: Sprint
}

const SprintList = () => {
    const [pageSize, setPageSize] = useState(10);
    const [value, setValue] = useState<string>('')
    const [editOpen, setEditOpen] = useState<boolean>(false)
    const list = SprintStore.useState(s => s.sprints);
    const [filteredList, setFilteredList] = useState([] as Sprint[])
    const [selectedSprint, setSelectedSprint] = useState(undefined as undefined | Sprint);
    const [filtersOpen, setFiltersOpen] = useState(false);
  
    const [filter, setFilter] = useState({
        id: uuid(),
        description: '',
        searchTerm: '',
        options: [],
        activityDate: 'today',
        filterType: FilterType.User
    } as Filter);
    const [status, execute, resolve, reject, reset] = useAwaitableComponent();
    const showModal = status !== "idle";

    useEffect(() => {
        loadRecords(0, pageSize);
    }, [filter])

    useEffect(() => {
        setFilteredList(list.filter(u => value == '' || (u.name.toUpperCase().includes(value.toUpperCase()) || u.playbookName.toUpperCase().includes(value.toUpperCase()))));
    }, [list, value])

    const onRefresh = () => {
        loadRecords(0, pageSize);
    }

    const handleConfirmDelete = async (row: Sprint) => {
        try {
            const value = await execute();
            const response = await client.delete<ApiResponseType>('/api/sprints', { params: { sprintId: row.id } });

            if (response.data.succeeded) {
                await loadRecords(0, pageSize);

                ApplicationStore.update(s => {
                    s.snackbarMessage = "Sprint successfully deleted";
                    s.snackbarOpen = true;
                });
            } else {
                alert('Error while deleting sprint.');
            }
        } catch (err) {

        } finally {
            reset();
        }
    };

    const columns = [
        {
            flex: 0.4,
            minWidth: 230,
            field: 'name',
            headerName: 'Sprint',
            renderCell: ({ row }: CellType) => {
                const { id, name } = row

                return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
                            <Typography
                                noWrap
                                component='a'
                                variant='body2'
                                sx={{ fontWeight: 600, color: 'text.primary', textDecoration: 'none' }}
                            >
                                {name}
                            </Typography>
                        </Box>
                    </Box>
                )
            }
        },
        {
            flex: 0.3,
            field: 'playbookName',
            headerName: 'Playbook',
            renderCell: ({ row }: CellType) => {
                return (
                    <Typography noWrap variant='body2'>
                        {row.playbookName}
                    </Typography>
                )
            }
        },
        {
            flex: 0.2,
            field: 'sprintStatusName',
            headerName: 'Status',
            renderCell: ({ row }: CellType) => {
                return (
                    <Typography noWrap variant='body2'>
                        {row.sprintStatusName}
                    </Typography>
                )
            }
        },
        {
            flex: 0.2,
            field: 'sprintTypeName',
            headerName: 'Type',
            renderCell: ({ row }: CellType) => {
                return (
                    <Typography noWrap variant='body2'>
                        {row.sprintTypeName}
                    </Typography>
                )
            }
        },
        {
            flex: 0.2,
            field: 'customerName',
            headerName: 'Customer',
            renderCell: ({ row }: CellType) => {
                return (
                    <Typography noWrap variant='body2'>
                        {row.customerName}
                    </Typography>
                )
            }
        },
        {
            flex: 0.2,
            field: 'startDate',
            headerName: 'Start Date',
            renderCell: ({ row }: CellType) => {
                return (
                    <DateTimeDisplay value={row.startDate} format='date' />
                )
            }
        },
        {
            flex: 0.2,
            field: 'endDate',
            headerName: 'End Date',
            renderCell: ({ row }: CellType) => {
                return (
                    <DateTimeDisplay value={row.endDate} format='date' />
                )
            }
        },
        {
            flex: 0.1,
            field: '',
            minWidth: 240,
            headerNamef: 'Actions',
            renderCell: ({ row }: CellType) => {
                return (
                    <>
                        <RenderIf claimType={ClaimType.Sprints} claimValue={ClaimValue.Delete}>
                            <IconButton onClick={() => handleConfirmDelete(row)} title="Delete">
                                <DeleteIcon fontSize='small' />
                            </IconButton>
                        </RenderIf>
                        <RenderIf claimType={ClaimType.Sprints} claimValue={ClaimValue.Edit}>
                            <SprintRowOptions id={row.id} status={row.sprintStatusName} onEdit={onEdit} onToggleActive={onToggleActive} row={row} />
                        </RenderIf>

                        <RoleSelector claimType={ClaimType.Sprints} recordId={row.id} />

                        <RenderIf claimType={ClaimType.Sprints} claimValue={ClaimValue.AssignLeads}>
                            <ICPWizard sprint={row} onClosed={onRefresh} />
                        </RenderIf>
                    </>
                )
            }
        },
    ]

    const onToggleActive = async (row: Sprint) => {
        //try {
        //    const response = await client.post<ApiResponseType>(toggleSprintStatusEndpoint, row)

        //    if (response.data.succeeded) {

        //    } else {
        //        ApplicationStore.update(s => {
        //            s.snackbarMessage = 'Error saving sprint ' + response.data.errors[0].code;
        //            s.snackbarOpen = true;
        //        })
        //    }
        //} catch (err) {
        //    ApplicationStore.update(s => {
        //        s.snackbarMessage = 'Error saving sprint ' + err.errors[0].code;
        //        s.snackbarOpen = true;
        //    })
        //}
    }

    const onEdit = (row: Sprint) => {
        setSelectedSprint(row);
        setEditOpen(true);
    }


    const loadRecords = async (pageNumber: number, pageSize: number) => {
        const response = await client.get<Sprint[]>(getSprintsEndpoint, { params: { pageNumber, pageSize } });

        SprintStore.update(s => {
            s.sprints = response.data;
        })

        // Remove
        //setSelectedSprint(response.data[0]);
    }

    const handleFilter = useCallback((val: string) => {
        setValue(val)
    }, [])

    const toggleEditSprintDrawer = async () => {
        if (editOpen) {
            await loadRecords(1, pageSize);
        }
        setEditOpen(!editOpen)
    }

    const onRowClick = (row: Sprint) => {
        setSelectedSprint(row);
    };

    const onNewSprint = () => {
        setSelectedSprint({
            id: uuidv4(),
            name: '',
            playbookId: '',
            customerId: '',
            startDate: '',
            endDate: '',
            sprintStatusId: '',
            sprintTypeId: ''
        } as Sprint);

        setEditOpen(true);
    }

    const onShowFilters = () => {
        setFiltersOpen(true);
    }

    const onCloseFilter = () => {
        setFiltersOpen(false);
    }

    const onFiltersChanged = (newFilter: Filter) => {
        setFilter(newFilter);
    }



    let filtersTemp = _.cloneDeep(filter);

    //if (thisSprintOnly && selectedSprint) {
    //    filtersTemp.options.push({
    //        id: uuid(),
    //        optionType: 'SPRINT',
    //        value: selectedSprint.id,
    //        name: '',
    //        inclusive: true,
    //        selected: true
    //    });
    //}


    return (
        <Grid container spacing={6}>
            <Grid item xs={12}>
                <PageHeader
                    title={<Typography variant='h5'>Sprint List</Typography>}
                    subtitle={
                        <Typography variant='body2'>
                            Create and manage sprints.
                        </Typography>
                    }
                />
            </Grid>
            <Grid item xs={12}>
                <Card>
                    <TableHeader value={value} handleFilter={handleFilter} toggle={onNewSprint} />
                    <DataGrid
                        autoHeight
                        pagination
                        rows={filteredList}
                        columns={columns}
                        pageSize={pageSize}
                        disableSelectionOnClick
                        rowsPerPageOptions={[10, 25, 50]}
                        onPageSizeChange={(newPageSize: number) => setPageSize(newPageSize)}
                    />
                </Card>
            </Grid>
            {selectedSprint && <Grid item xs={12}>
                <EditSprintDrawer open={editOpen} toggle={toggleEditSprintDrawer} sprint={selectedSprint} />
            </Grid>}

            {/*<RenderIf claimType={ClaimType.Sprints} claimValue={ClaimValue.AssignLeads}>*/}
            {/*    {selectedSprint && <Grid item xs={10}>*/}
            {/*        <LeadSearch onChange={onFiltersChanged} filterType={FilterType.Playbook} />*/}
            {/*    </Grid>}*/}

            {/*    {selectedSprint && <Grid item xs={2}>*/}
            {/*        <FormControl component="fieldset" variant="standard">*/}
            {/*            <FormGroup>*/}
            {/*                <FormControlLabel*/}
            {/*                    control={*/}
            {/*                        <Switch checked={unassignedOnly} onChange={handleChange} name="gilad" />*/}
            {/*                    }*/}
            {/*                    label="Unassigned only"*/}
            {/*                />*/}
            {/*            </FormGroup>*/}
            {/*        </FormControl>*/}
            {/*    </Grid>}*/}
            {/*    <Grid item xs={12}>*/}
            {/*        {selectedSprint && <LeadList filter={filtersTemp} selectedSprintId={selectedSprint.id} onLeadSelected={(leadId: string) => { alert(leadId) }} unassignedOnly={unassignedOnly} />}*/}
            {/*    </Grid>*/}
            {/*</RenderIf>*/}

            <ConfirmationDialog open={showModal} onConfirm={resolve} onCancel={reject} title="Delete?" message="Are you sure you want to delete the sprint?" confirmButtonText="Delete" cancelButtonText="Cancel" />
        </Grid>

    )
}

export { SprintList }
export default SprintList
